import React, { useEffect, useRef, useState } from "react";
import ScrollerWithArrows from "../../../Simplify360Chat/CommonUtils/ScrollerWithArrows";
import "./BoxDetailsCard.css";

const DEFAULT_BOX_IMAGE = "https://help.tataplay.com/uploads/gbmimages/HD_Box_Creative.jpg";

const InactiveStatusIcon = () => (
  <svg
    className="inactive-status-icon"
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <mask
      id="inactive-status-mask"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="16"
      height="16"
    >
      <rect width="16" height="16" fill="#E10092" />
    </mask>
    <g mask="url(#inactive-status-mask)">
      <path
        d="M4.52741 11.5363L11.4649 8.75682V7.26548L4.52741 4.48619V5.82369L10.5189 8.01126L4.52741 10.1988V11.5363Z"
        fill="#E10092"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 8C0 3.58138 3.58183 0 8 0C12.4182 0 16 3.58138 16 8C16 12.4186 12.4182 16 8 16C3.58183 16 0 12.4186 0 8ZM12.8639 2.60163C11.5318 1.3935 9.79839 0.723213 8 0.720782C7.02676 0.718794 6.06314 0.913311 5.16688 1.29268C3.51025 1.99254 2.17262 3.28275 1.41343 4.91303C0.654249 6.54332 0.527635 8.39748 1.05815 10.1158C1.58867 11.8342 2.7385 13.2943 4.28461 14.2128C5.83071 15.1314 7.66288 15.443 9.42571 15.0871C11.1885 14.7313 12.7564 13.7334 13.8251 12.287C14.8938 10.8406 15.3872 9.04881 15.2096 7.25921C15.032 5.46961 14.1961 3.80976 12.8639 2.60163Z"
        fill="#E10092"
      />
    </g>
  </svg>
);

const BoxDetailsCard = ({ data, tag, uiCommand }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [boxDetails, setBoxDetails] = useState([]);
  const [subscriberInfo, setSubscriberInfo] = useState({});
  const [activeSlide, setActiveSlide] = useState(0);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const cardsContainerRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    if (!data) return;

    try {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      const boxes = parsedData.boxDetails || [];
      setBoxDetails(boxes);
      setSubscriberInfo({
        subscriberId: parsedData.subscriberId || "",
        accountBalance: parsedData.currentAccountBalance || parsedData.accountBalance || "0",
        nextDueDate: parsedData.balanceDueDate || parsedData.nextDueDate || "",
      });
      setActiveSlide(0);
      setShowLeft(false);
      setShowRight(boxes.length > 1);
    } catch (error) {
      console.error("Error parsing box details data:", error);
    }
  }, [data]);

  useEffect(() => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollLeft = 0;
    }
    setShowLeft(false);
    setShowRight(boxDetails.length > 1);
  }, [boxDetails]);

  const formatCurrency = (amount) => {
    const value = String(amount || 0).replace(/[^\d.-]/g, "");
    return `₹${parseFloat(value || 0).toFixed(2)}`;
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    return dueDate < today;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { month: "short", day: "2-digit", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const getBoxTypeLabel = (index) => {
    if (index === 0) return "Primary Box";
    return `Secondary Box${index}`;
  };

  const isBoxActive = (box) => {
    return String(box.boxStatus || "Active").toLowerCase() === "active";
  };

  const getBoxDisplayName = (box) => {
    return box.boxDisplayName || box.boxServiceType || box.planName || box.serviceType || "Tata Play Binge+";
  };

  const getBoxMonthlyCharge = (box) => {
    return box.monthlyCharge ?? 0;
  };

  const getBoxImage = (box) => {
    const imageSource = String(box.imageLink || box.image || box.boxType || "").trim();
    return imageSource.startsWith("http") ? imageSource : DEFAULT_BOX_IMAGE;
  };

  const handleBoxImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = DEFAULT_BOX_IMAGE;
  };

  const getScrollStep = () => {
    const container = cardsContainerRef.current;
    if (!container) return 0;

    const card = container.querySelector(".box-card");
    const gap = parseFloat(window.getComputedStyle(container).gap) || 0;
    return card ? card.offsetWidth + gap : container.clientWidth / 2;
  };

  const handleScroll = () => {
    const container = cardsContainerRef.current;
    if (!container) return;

    const scrollStep = getScrollStep();
    const nextSlide = scrollStep
      ? Math.min(
          Math.round(container.scrollLeft / scrollStep),
          Math.max(boxDetails.length - 1, 0)
        )
      : 0;
    const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);

    setActiveSlide(nextSlide);
    setShowLeft(nextSlide > 0 && container.scrollLeft > 2);
    setShowRight(container.scrollLeft < maxScrollLeft - 2);
  };

  const isSingleBox = boxDetails.length === 1;
  const isTwoBoxLayout = boxDetails.length === 2;
  const shouldShowSecondaryPager = boxDetails.length > 1;
  const isA3DE = tag === "A3DE" || uiCommand === "A3DE";

  const renderCardsContainer = () => (
    <div
      id="boxDetailsScroll"
      ref={cardsContainerRef}
      className={`box-cards-container ${isSingleBox ? "single-box-layout" : ""}`}
      onScroll={isSingleBox ? undefined : handleScroll}
    >
      {boxDetails.map((box, index) => {
        const isPrimaryBox = index === 0;
        const shouldShowStatusIcon = isPrimaryBox || box.boxStatus;

        return (
          <div
            key={box.boxId || index}
            id={`boxDetailsCard-${index}`}
            className={`box-card ${isPrimaryBox ? "primary-box" : "secondary-box"} ${
              isTwoBoxLayout
                ? isPrimaryBox
                  ? "leading-box"
                  : "trailing-box"
                : index === 0
                ? "leading-box"
                : "trailing-box"
            } ${isSingleBox ? "single-box-card" : ""}`}
          >
            <div className="box-card-header">
              <span className="box-type-label">
                {shouldShowStatusIcon && (
                  isBoxActive(box) ? (
                    <span className="primary-check">✓</span>
                  ) : (
                    <InactiveStatusIcon />
                  )
                )}
                <span className="box-type-text">
                  {getBoxTypeLabel(index)}
                </span>
              </span>
            </div>

            <div className="box-id-row">
              <span className="box-id-label">Box ID:</span>
              <span className="box-id-value">
                {String(box.boxId || "").slice(-4)}
              </span>
            </div>

            <div className="box-image-container">
              <img
                src={getBoxImage(box)}
                alt="Set Top Box"
                onError={handleBoxImageError}
              />
            </div>

            <div className="box-plan-name">
              {getBoxDisplayName(box)}
            </div>

            <div className="box-details-grid">
              <div className="detail-row channels-row">
                <span className="detail-label">Channels</span>
                <span className="detail-value">{box.channelsCount || 0}</span>
              </div>
              {(box.appsCount || box.ottApps) > 0 && (
                <div className="detail-row">
                  <span className="detail-label">OTT Apps</span>
                  <span className="detail-value">{box.appsCount || box.ottApps}</span>
                </div>
              )}
              <div className="detail-row monthly-row">
                <span className="detail-label">Monthly charges</span>
                <span className="detail-value monthly-value">
                  {formatCurrency(getBoxMonthlyCharge(box))}
                </span>
              </div>
            </div>

            {isPrimaryBox && box.includedServices && (
              <div className="included-services">
                <span className="included-badge">{box.includedServices}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`box-details-wrapper ${isVisible ? "box-details-animate" : ""} ${
        isSingleBox ? "single-box-details" : ""
      } ${isA3DE ? "a3de-box-details" : ""}`}
    >
      <div className="box-details-container">
        <div className="account-details-parent">
          <div className="subscriber-header">
            <div className="subscriber-id">
              <span className="label">Subscriber ID:</span>
              <span className="value">{subscriberInfo.subscriberId}</span>
            </div>
            <div className="subscriber-info-row">
              <div className="account-balance">
                <span className="label">Account Balance</span>
                <span className="balance-value">
                  {formatCurrency(subscriberInfo.accountBalance)}
                </span>
              </div>
              <div className="next-due-date">
                <span className="label">Next Due On</span>
                <span className={`due-date-value ${isOverdue(subscriberInfo.nextDueDate) ? "overdue" : ""}`}>
                  {isOverdue(subscriberInfo.nextDueDate) && (
                    <span className="box-warning-icon">⚠</span>
                  )}
                  {formatDate(subscriberInfo.nextDueDate)}
                  <span className="box-info-icon">ⓘ</span>
                </span>
              </div>
            </div>
          </div>

          <div className="box-cards-outer">
            {isSingleBox ? (
              renderCardsContainer()
            ) : (
              <ScrollerWithArrows
                width="100%"
                isShowLeft={activeSlide > 0 && showLeft}
                isShowRight={showRight}
                setShowLeft={setShowLeft}
                setShowRight={setShowRight}
                lastIndex={Math.max(boxDetails.length - 1, 0)}
                parentId="boxDetailsScroll"
                childId="boxDetailsCard"
                minimumValue={88}
                maximumValue={160}
                dontShow={false}
                arrowVariant={isA3DE ? "plain" : undefined}
              >
                {renderCardsContainer()}
              </ScrollerWithArrows>
            )}

            {shouldShowSecondaryPager && (
              <div className="main-pagination">
                {boxDetails.map((_, index) => (
                  <span
                    key={index}
                    className={`pagination-dot ${index === activeSlide ? "active" : ""}`}
                  ></span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxDetailsCard;
